// rsaPathsReducer.js — Pure reducer for RSA multi-path / multi-lane state.
// No React imports. All mutations go through here.

import { createInitialState } from './rsaPathTypes';

// ─── Action type constants ────────────────────────────────────────────────────

export const PATH_ACTIONS = {
  // Path operations
  ADD_PATH: 'ADD_PATH',
  UPDATE_PATH: 'UPDATE_PATH',
  DELETE_PATH: 'DELETE_PATH',
  SET_ACTIVE_PATH: 'SET_ACTIVE_PATH',

  // Lane operations
  ADD_LANE: 'ADD_LANE',
  UPDATE_LANE: 'UPDATE_LANE',
  DELETE_LANE: 'DELETE_LANE',
  SET_ACTIVE_LANE: 'SET_ACTIVE_LANE',

  // Intersection operations
  ADD_INTERSECTION: 'ADD_INTERSECTION',
  UPDATE_INTERSECTION: 'UPDATE_INTERSECTION',
  DELETE_INTERSECTION: 'DELETE_INTERSECTION',
  SET_ACTIVE_INTERSECTION: 'SET_ACTIVE_INTERSECTION',

  // Lane edit mode (rubber-band select)
  SET_LANE_EDIT_MODE: 'SET_LANE_EDIT_MODE',
  SET_SELECTED_LANE_WAYPOINTS: 'SET_SELECTED_LANE_WAYPOINTS',

  // Speed edit mode
  SET_SPEED_EDIT_MODE: 'SET_SPEED_EDIT_MODE',

  // File mapping (XML → JSON path file)
  SET_XML_TO_JSON_MAP: 'SET_XML_TO_JSON_MAP',

  // Global
  SET_ACTIVE_ENTITY: 'SET_ACTIVE_ENTITY',
  HYDRATE: 'HYDRATE',
  RESET: 'RESET',
};

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function rsaPathsReducer(state, action) {
  switch (action.type) {

    // ── Paths ──────────────────────────────────────────────────────────────

    case PATH_ACTIONS.ADD_PATH: {
      return { ...state, paths: [...state.paths, action.path] };
    }

    case PATH_ACTIONS.UPDATE_PATH: {
      return {
        ...state,
        paths: state.paths.map((p) =>
          p.id === action.id ? { ...p, ...action.updates } : p
        ),
      };
    }

    case PATH_ACTIONS.DELETE_PATH: {
      const remainingPaths = state.paths.filter((p) => p.id !== action.id);
      const remainingLanes = state.lanes.filter((l) => l.pathId !== action.id);
      const deletedLaneIds = new Set(
        state.lanes.filter((l) => l.pathId === action.id).map((l) => l.id),
      );
      const remainingIntersections = (state.intersections ?? []).filter(
        (i) => !deletedLaneIds.has(i.rsaPathId),
      );
      const surviveIntersection = remainingIntersections.some(
        (i) => i.id === state.activeIntersectionId,
      );
      const activeLaneStillExists = remainingLanes.some((l) => l.id === state.activeLaneId);
      const newActivePathId =
        state.activePathId === action.id
          ? (remainingPaths[0]?.id ?? null)
          : state.activePathId;
      return {
        ...state,
        paths: remainingPaths,
        lanes: remainingLanes,
        intersections: remainingIntersections,
        activePathId: newActivePathId,
        activeLaneId: activeLaneStillExists ? state.activeLaneId : null,
        activeIntersectionId: surviveIntersection ? state.activeIntersectionId : null,
        activeEntityType:
          state.activeEntityType === 'lane' && !activeLaneStillExists
            ? 'path'
            : state.activeEntityType,
        laneEditMode: activeLaneStillExists ? state.laneEditMode : false,
        speedEditMode: activeLaneStillExists ? state.speedEditMode : false,
        selectedLaneWaypointIndices: activeLaneStillExists
          ? state.selectedLaneWaypointIndices
          : [],
      };
    }

    case PATH_ACTIONS.SET_ACTIVE_PATH: {
      // Always switch to path-as-active, clearing any lane selection.
      // (Previously, clicking a path while one of its lanes was active was a
      // no-op because the path was technically already "active" via the
      // lane's pathId — that prevented switching from lane back to Route.)
      return {
        ...state,
        activePathId: action.id,
        activeEntityType: 'path',
        activeLaneId: null,
        laneEditMode: false,
        speedEditMode: false,
        selectedLaneWaypointIndices: [],
      };
    }

    // ── Lanes ──────────────────────────────────────────────────────────────

    case PATH_ACTIONS.ADD_LANE: {
      return { ...state, lanes: [...state.lanes, action.lane] };
    }

    case PATH_ACTIONS.UPDATE_LANE: {
      return {
        ...state,
        lanes: state.lanes.map((l) =>
          l.id === action.id ? { ...l, ...action.updates } : l
        ),
      };
    }

    case PATH_ACTIONS.DELETE_LANE: {
      const remainingLanes = state.lanes.filter((l) => l.id !== action.id);
      const wasActive = state.activeLaneId === action.id;
      return {
        ...state,
        lanes: remainingLanes,
        activeLaneId: wasActive ? null : state.activeLaneId,
        activeEntityType: wasActive ? 'path' : state.activeEntityType,
        laneEditMode: wasActive ? false : state.laneEditMode,
        speedEditMode: wasActive ? false : state.speedEditMode,
        selectedLaneWaypointIndices: wasActive ? [] : state.selectedLaneWaypointIndices,
      };
    }

    case PATH_ACTIONS.SET_ACTIVE_LANE: {
      const activeLane = state.lanes.find((lane) => lane.id === action.id);
      return {
        ...state,
        activePathId: activeLane?.pathId ?? state.activePathId,
        activeLaneId: action.id,
        activeEntityType: 'lane',
        laneEditMode: false,
        speedEditMode: false,
        selectedLaneWaypointIndices: [],
      };
    }

    // ── Intersections ───────────────────────────────────────────────────────

    case PATH_ACTIONS.ADD_INTERSECTION: {
      return { ...state, intersections: [...state.intersections, action.intersection] };
    }

    case PATH_ACTIONS.UPDATE_INTERSECTION: {
      return {
        ...state,
        intersections: state.intersections.map((i) =>
          i.id === action.id ? { ...i, ...action.updates } : i
        ),
      };
    }

    case PATH_ACTIONS.DELETE_INTERSECTION: {
      const remaining = state.intersections.filter((i) => i.id !== action.id);
      const wasActive = state.activeIntersectionId === action.id;
      return {
        ...state,
        intersections: remaining,
        activeIntersectionId: wasActive ? (remaining[0]?.id ?? null) : state.activeIntersectionId,
      };
    }

    case PATH_ACTIONS.SET_ACTIVE_INTERSECTION: {
      return { ...state, activeIntersectionId: action.id };
    }

    // ── Lane edit mode ─────────────────────────────────────────────────────

    case PATH_ACTIONS.SET_LANE_EDIT_MODE: {
      return {
        ...state,
        laneEditMode: action.enabled,
        speedEditMode: false,
        selectedLaneWaypointIndices: action.enabled ? state.selectedLaneWaypointIndices : [],
      };
    }

    case PATH_ACTIONS.SET_SELECTED_LANE_WAYPOINTS: {
      return { ...state, selectedLaneWaypointIndices: action.indices };
    }

    // ── Speed edit mode ───────────────────────────────────────────────────
    case PATH_ACTIONS.SET_SPEED_EDIT_MODE: {
      return {
        ...state,
        speedEditMode: action.enabled,
      };
    }

    // ── File mapping ────────────────────────────────────────────────────

    case PATH_ACTIONS.SET_XML_TO_JSON_MAP: {
      return { ...state, xmlToJsonMap: action.map || {} };
    }

    // ── Global ─────────────────────────────────────────────────────────────

    case PATH_ACTIONS.SET_ACTIVE_ENTITY: {
      const isPath = action.entityType === 'path';
      const activeLane = isPath
        ? null
        : state.lanes.find((lane) => lane.id === action.id);
      return {
        ...state,
        activeEntityType: action.entityType,
        activePathId: isPath ? action.id : (activeLane?.pathId ?? state.activePathId),
        activeLaneId: isPath ? null : action.id,
        laneEditMode: false,
        speedEditMode: false,
        selectedLaneWaypointIndices: [],
      };
    }

    case PATH_ACTIONS.HYDRATE: {
      const hydrated = {
        ...createInitialState(),
        ...action.state,
        intersections: action.state.intersections ?? [],
      };

      const incomingPaths = Array.isArray(hydrated.paths) ? hydrated.paths : [];
      const incomingLanes = Array.isArray(hydrated.lanes) ? hydrated.lanes : [];
      const activeLane = state.activeLaneId
        ? incomingLanes.find((lane) => lane.id === state.activeLaneId)
        : null;
      const activePathStillExists =
        state.activePathId != null &&
        incomingPaths.some((path) => path.id === state.activePathId);

      if (activeLane) {
        return {
          ...hydrated,
          activePathId: activeLane.pathId ?? hydrated.activePathId,
          activeLaneId: state.activeLaneId,
          activeEntityType: 'lane',
          laneEditMode: state.laneEditMode,
          speedEditMode: state.speedEditMode,
          selectedLaneWaypointIndices: state.selectedLaneWaypointIndices,
        };
      }

      if (state.activeEntityType === 'path' && activePathStillExists) {
        return {
          ...hydrated,
          activePathId: state.activePathId,
          activeLaneId: null,
          activeEntityType: 'path',
        };
      }

      return hydrated;
    }

    case PATH_ACTIONS.RESET: {
      return createInitialState();
    }

    default:
      return state;
  }
}
